import { useEffect, useState, useCallback } from "react";
import AppLayout from "@/components/AppLayout";
import { AdminSidebar } from "@/components/sidebars";
import { apiClient } from "@/lib/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-MX") : "—");
const shortDay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
};

function GrowthChart() {
  const [series, setSeries] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get(`/admin/security/growth?days=${days}`)
      .then(({ data }) => { if (!cancelled) setSeries(data.series || []); })
      .catch(() => { if (!cancelled) setSeries([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const totalRegistros = series.reduce((acc, p) => acc + (p.registros || 0), 0);
  const totalRL = series.reduce((acc, p) => acc + (p.rate_limit || 0), 0);
  const avgPerDay = series.length ? (totalRegistros / series.length).toFixed(1) : "0";

  return (
    <div className="panel" style={{ padding: 18, marginBottom: 22 }} data-testid="growth-chart">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, color: "#163680" }}>Crecimiento diario</h3>
          <small style={{ color: "#6b7280" }}>
            {totalRegistros} registros en {days} días · promedio {avgPerDay}/día · {totalRL} hits de rate-limit
          </small>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              data-testid={`growth-range-${d}`}
              className={`btn ${days === d ? "btn-primary" : "btn-secondary"}`}
              style={{ fontSize: 12, padding: "5px 12px" }}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <p className="text-muted">Cargando gráfico…</p>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={series} margin={{ top: 6, right: 18, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#eef2f7" strokeDasharray="3 3" />
            <XAxis dataKey="date" tickFormatter={shortDay} tick={{ fontSize: 11, fill: "#6b7280" }} interval={Math.max(0, Math.floor(series.length / 10))} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} />
            <Tooltip
              labelFormatter={(d) => shortDay(d)}
              contentStyle={{ borderRadius: 8, border: "1px solid #e7eaf2", fontSize: 13 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="registros" name="Registros" stroke="#163680" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
            <Line type="monotone" dataKey="rate_limit" name="Rate-limit" stroke="#E84C5E" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function StatCard({ label, value, color = "#163680", icon }) {
  return (
    <div className="panel" data-testid={`stat-${label}`} style={{ padding: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <i className={icon} style={{ fontSize: 24, color }} />
        <div>
          <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
          <div style={{ fontSize: 26, fontWeight: 700, color }}>{value}</div>
        </div>
      </div>
    </div>
  );
}

function Table({ columns, rows, empty }) {
  if (!rows?.length) return <p className="text-muted" style={{ padding: 16 }}>{empty}</p>;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ background: "#f8fafc", color: "#163680" }}>
          {columns.map((c) => (
            <th key={c.key} style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid #e7eaf2", fontWeight: 700, fontSize: 11, letterSpacing: 0.4, textTransform: "uppercase" }}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={{ borderBottom: "1px solid #f1f4f8" }}>
            {columns.map((c) => (
              <td key={c.key} style={{ padding: "10px 12px", color: "#374151" }}>
                {c.render ? c.render(r) : r[c.key] ?? "—"}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AlertsPanel() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/security/alerts?limit=20");
      setAlerts(data || []);
    } catch {
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const evaluateNow = async () => {
    setEvaluating(true);
    try {
      await apiClient.post("/admin/security/alerts/evaluate");
      await load();
    } finally {
      setEvaluating(false);
    }
  };

  const ackType = async (type) => {
    try {
      await apiClient.post(`/admin/security/alerts/${type}/ack`);
      await load();
    } catch { /* ignore */ }
  };

  const sev = {
    critical: { bg: "#FEE2E2", color: "#B91C1C", icon: "fas fa-exclamation-triangle" },
    warning: { bg: "#FEF3C7", color: "#92400E", icon: "fas fa-exclamation-circle" },
    info: { bg: "#E7EBF5", color: "#163680", icon: "fas fa-info-circle" },
  };

  const unacked = alerts.filter((a) => !a.acknowledged);

  return (
    <div className="panel" style={{ marginBottom: 22, padding: 0 }} data-testid="alerts-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px 8px", flexWrap: "wrap", gap: 8 }}>
        <div>
          <h3 style={{ margin: 0, color: "#163680" }}>
            Alertas anómalas
            {unacked.length > 0 && (
              <span style={{ background: "#B91C1C", color: "white", padding: "2px 8px", borderRadius: 999, fontSize: 11, marginLeft: 8, fontWeight: 700 }}>
                {unacked.length} sin revisar
              </span>
            )}
          </h3>
          <small style={{ color: "#6b7280" }}>
            Se evalúa cada 5 min en background. Email al admin cuando se dispara.
          </small>
        </div>
        <button
          type="button"
          onClick={evaluateNow}
          disabled={evaluating}
          className="btn btn-secondary"
          style={{ fontSize: 12, padding: "6px 14px" }}
          data-testid="alerts-evaluate-btn"
        >
          {evaluating ? "Evaluando…" : "Evaluar ahora"}
        </button>
      </div>
      {loading ? (
        <p className="text-muted" style={{ padding: 16 }}>Cargando…</p>
      ) : alerts.length === 0 ? (
        <p className="text-muted" style={{ padding: 16 }}>✅ Sin alertas. Todo tranquilo.</p>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: "0 18px 14px" }}>
          {alerts.slice(0, 10).map((a, i) => {
            const s = sev[a.severity] || sev.info;
            return (
              <li
                key={i}
                style={{ padding: "10px 12px", borderRadius: 8, background: s.bg, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}
                data-testid={`alert-${i}`}
              >
                <div style={{ flex: 1, minWidth: 280 }}>
                  <div style={{ fontWeight: 700, color: s.color, fontSize: 13, marginBottom: 2 }}>
                    <i className={s.icon} style={{ marginRight: 6 }} /> {a.type}
                    {a.acknowledged && <span style={{ marginLeft: 8, fontSize: 11, color: "#6b7280", fontWeight: 400 }}>✓ revisado</span>}
                  </div>
                  <div style={{ fontSize: 13, color: "#374151" }}>{a.message}</div>
                  <small style={{ color: "#6b7280" }}>
                    Valor: <strong>{a.value}</strong> · Umbral: {a.threshold} · {fmt(a.fired_at)}
                  </small>
                </div>
                {!a.acknowledged && (
                  <button
                    type="button"
                    onClick={() => ackType(a.type)}
                    className="btn btn-secondary"
                    style={{ fontSize: 11, padding: "5px 12px" }}
                    data-testid={`alert-ack-${a.type}`}
                  >
                    Marcar revisado
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


export default function AdminSecurity() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/security/dashboard");
      setData(data);
    } catch {
      setData({ error: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  if (loading && !data) return (
    <AppLayout sidebar={AdminSidebar}>
      <p className="text-muted">Cargando…</p>
    </AppLayout>
  );

  if (data?.error) return (
    <AppLayout sidebar={AdminSidebar}>
      <p style={{ color: "#B91C1C" }}>No se pudo cargar el dashboard.</p>
    </AppLayout>
  );

  const t = data?.totals || {};

  return (
    <AppLayout sidebar={AdminSidebar}>
      <header className="app-header">
        <h1><i className="fas fa-shield-alt" style={{ color: "#163680", marginRight: 10 }} />Seguridad</h1>
        <p className="text-muted">
          Métricas en tiempo real de intentos de abuso y eventos sensibles. Actualiza cada 30 s.
        </p>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 22 }}>
        <StatCard label="Usuarios totales" value={t.users ?? 0} icon="fas fa-users" />
        <StatCard label="Nuevos 24h" value={t.new_users_24h ?? 0} icon="fas fa-user-plus" color="#15803D" />
        <StatCard label="Cuentas bloqueadas" value={t.active_lockouts ?? 0} icon="fas fa-lock" color="#B91C1C" />
        <StatCard label="Rate-limit (1h)" value={t.rate_limited_last_hour ?? 0} icon="fas fa-bolt" color="#E84C5E" />
        <StatCard label="🔥 Leads WhatsApp" value={t.wa_hot_leads ?? 0} icon="fab fa-whatsapp" color="#25D366" />
      </div>

      <GrowthChart />

      <AlertsPanel />

      <div className="panel" style={{ marginBottom: 22, padding: 0 }} data-testid="lockouts-panel">
        <h3 style={{ padding: "14px 18px 0", color: "#163680" }}>Cuentas con bloqueo activo (brute-force)</h3>
        <Table
          empty="Sin bloqueos activos."
          columns={[
            { key: "identifier", label: "IP : Email" },
            { key: "count", label: "Intentos" },
            { key: "locked_until", label: "Desbloquea", render: (r) => fmt(r.locked_until) },
            { key: "updated_at", label: "Último intento", render: (r) => fmt(r.updated_at) },
          ]}
          rows={data?.active_lockouts || []}
        />
      </div>

      <div className="panel" style={{ marginBottom: 22, padding: 0 }} data-testid="ratelimit-panel">
        <h3 style={{ padding: "14px 18px 0", color: "#163680" }}>Top IPs limitadas (última hora)</h3>
        <Table
          empty="Sin actividad sospechosa en la última hora."
          columns={[
            { key: "ip", label: "IP" },
            { key: "key", label: "Endpoint" },
            { key: "count", label: "Hits" },
            { key: "last_at", label: "Último hit", render: (r) => fmt(r.last_at) },
          ]}
          rows={data?.rate_limit_hits || []}
        />
      </div>

      <div className="panel" style={{ padding: 0 }} data-testid="audit-panel">
        <h3 style={{ padding: "14px 18px 0", color: "#163680" }}>Audit log reciente (datos médicos)</h3>
        <Table
          empty="Sin eventos de auditoría."
          columns={[
            { key: "timestamp", label: "Cuándo", render: (r) => fmt(r.timestamp) },
            { key: "actor", label: "Actor", render: (r) => r.actor?.email || "—" },
            { key: "action", label: "Acción" },
            { key: "resource_type", label: "Tipo" },
            { key: "ip", label: "IP" },
          ]}
          rows={data?.recent_audit || []}
        />
      </div>
    </AppLayout>
  );
}
